import { useEffect, useMemo, useState } from "react";
import {
  getKnowledgeBaseArticleById,
  getKnowledgeBaseArticles,
} from "../../services/knowledgeBaseApi";

export default function KnowledgeBasePage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedArticleId, setSelectedArticleId] = useState("");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [articleLoading, setArticleLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadArticles() {
      try {
        setListLoading(true);
        setError("");

        const res = await getKnowledgeBaseArticles({
          q: query.trim() || undefined,
          category: categoryFilter !== "all" ? categoryFilter : undefined,
        });

        if (!active) return;

        const nextItems = Array.isArray(res?.items) ? res.items : [];
        const nextCategories = Array.isArray(res?.categories) ? res.categories : [];

        setItems(nextItems);
        setCategories(nextCategories);

        if (!nextItems.length) {
          setSelectedArticleId("");
          setSelectedArticle(null);
          return;
        }

        const hasSelection = nextItems.some((item) => String(item?.id || "") === String(selectedArticleId || ""));
        if (!hasSelection) {
          setSelectedArticleId(String(nextItems[0]?.id || ""));
        }
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || "Kennisbank laden mislukt");
      } finally {
        if (active) setListLoading(false);
      }
    }

    loadArticles();
    return () => {
      active = false;
    };
  }, [query, categoryFilter, selectedArticleId]);

  useEffect(() => {
    let active = true;

    async function loadArticle() {
      if (!selectedArticleId) {
        setSelectedArticle(null);
        return;
      }

      try {
        setArticleLoading(true);
        setError("");
        const article = await getKnowledgeBaseArticleById(selectedArticleId);
        if (!active) return;
        setSelectedArticle(article);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || "Artikel laden mislukt");
        setSelectedArticle(null);
      } finally {
        if (active) setArticleLoading(false);
      }
    }

    loadArticle();
    return () => {
      active = false;
    };
  }, [selectedArticleId]);

  const categoryOptions = useMemo(
    () => ["all", ...(Array.isArray(categories) ? categories : [])],
    [categories]
  );

  return (
    <div className="space-y-4">
      <header className="rounded-xl bg-white p-4 shadow">
        <h2 className="text-xl font-semibold text-slate-900">Kennisbank</h2>
        <p className="mt-1 text-sm text-slate-600">
          Handleidingen en uitleg voor werkbonnen, export/import en beheer.
        </p>
      </header>

      <section className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <aside className="rounded-xl bg-white p-4 shadow">
          <div className="mb-3 space-y-2">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Zoek artikel"
              className="w-full rounded border border-slate-300 p-2 text-sm"
            />
            <select
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
              className="w-full rounded border border-slate-300 p-2 text-sm"
            >
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category === "all" ? "Alle categorieen" : category}
                </option>
              ))}
            </select>
          </div>

          {listLoading ? <p className="text-sm text-slate-500">Artikelen laden...</p> : null}
          {!listLoading && !items.length ? (
            <p className="text-sm text-slate-500">Geen artikelen gevonden.</p>
          ) : null}

          <div className="max-h-[65vh] space-y-2 overflow-auto pr-1">
            {items.map((item) => {
              const isActive = String(item?.id || "") === String(selectedArticleId || "");
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedArticleId(String(item.id || ""))}
                  className={`w-full rounded-lg border p-3 text-left transition ${
                    isActive
                      ? "border-blue-300 bg-blue-50"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <p className="text-sm font-medium text-slate-900">{item.title}</p>
                  <p className="mt-1 text-xs text-slate-600">{item.summary}</p>
                  <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-slate-500">
                    <span>{item.category}</span>
                    <span>{formatDate(item.lastUpdated)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <article className="rounded-xl bg-white p-4 shadow">
          {error ? (
            <p className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">
              {error}
            </p>
          ) : null}

          {articleLoading ? <p className="text-sm text-slate-500">Artikel laden...</p> : null}

          {!articleLoading && !selectedArticle ? (
            <p className="text-sm text-slate-500">Selecteer een artikel om te openen.</p>
          ) : null}

          {!articleLoading && selectedArticle ? (
            <>
              <div className="mb-4 border-b border-slate-100 pb-3">
                <h3 className="text-lg font-semibold text-slate-900">{selectedArticle.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{selectedArticle.summary}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span className="rounded bg-slate-100 px-2 py-1">{selectedArticle.category}</span>
                  <span>Bijgewerkt: {formatDate(selectedArticle.lastUpdated)}</span>
                </div>
              </div>

              <div className="max-h-[68vh] overflow-auto pr-1">
                <MarkdownLikeContent text={selectedArticle.content} />
              </div>
            </>
          ) : null}
        </article>
      </section>
    </div>
  );
}

function MarkdownLikeContent({ text }) {
  const lines = String(text || "").split(/\r?\n/);
  const nodes = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = String(lines[index] || "");

    if (!line.trim()) {
      nodes.push(<div key={`space-${index}`} className="h-3" />);
      continue;
    }

    if (/^---+$/.test(line.trim())) {
      nodes.push(<hr key={`hr-${index}`} className="my-3 border-slate-200" />);
      continue;
    }

    const h1 = line.match(/^#\s+(.+)$/);
    if (h1) {
      nodes.push(
        <h1 key={`h1-${index}`} className="mt-4 text-xl font-semibold text-slate-900">
          {h1[1]}
        </h1>
      );
      continue;
    }

    const h2 = line.match(/^##\s+(.+)$/);
    if (h2) {
      nodes.push(
        <h2 key={`h2-${index}`} className="mt-4 text-lg font-semibold text-slate-900">
          {h2[1]}
        </h2>
      );
      continue;
    }

    const h3 = line.match(/^###\s+(.+)$/);
    if (h3) {
      nodes.push(
        <h3 key={`h3-${index}`} className="mt-3 text-base font-semibold text-slate-900">
          {h3[1]}
        </h3>
      );
      continue;
    }

    const bullet = line.match(/^[-*]\s+(.+)$/);
    if (bullet) {
      nodes.push(
        <p key={`li-${index}`} className="text-sm leading-6 text-slate-700">
          • {bullet[1]}
        </p>
      );
      continue;
    }

    nodes.push(
      <p key={`p-${index}`} className="text-sm leading-6 text-slate-700">
        {line}
      </p>
    );
  }

  return <div>{nodes}</div>;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}
